/**
 * Defines behavior Event prototypes.
 * All non-temporal fields of an event, such as name, type, and tags are
 * stored in EventBaseClass.
 * The Event has the timestamp, session id, and a reference to an EventBaseClass.
 * This structure allows sharing of the same EventBase among several different
 * events.
 * For a brief analysis on space efficiency of the new format, see:
 *
 * Ported from [behavior_event@ab9709a52d](https://github.com/optimizely/optimizely/blob/ab9709a52d9b2579c72d436556b1d2c3c4276a6c/src/www/js/client/behavior_event.js)
 * @module
 */

var _ = require('optimizely-client-fn');

var enums = require('./enums');
var getFieldValue = require('../utils/key_path').getFieldValue;
var hashing = require('optimizely-hashing');

/**
 * Base object of event.
 * @param {string} name Event name.
 * @param {string} type Event type (E.g. 'pageview', 'click', 'custom')
 * @param {string} [category] Event category (E.g. 'product_detail', 'checkout')
 * @param {Object} [options] Options (contains tags).
 * @param {number} [revenue] Optional revenue value.
 * @constructor
 * @alias module:behavior/event~EventBase
 */
function EventBaseClass(name:string, type:string, category?:string, options?:any, revenue?:number) {
  this[enums.FIELDS.NAME] = name;
  this[enums.FIELDS.TYPE] = type;

  if (_.isString(category) && category.trim().length > 0) {
    this[enums.FIELDS.CATEGORY] = category;
  }

  if (options && _.keys(options).length > 0) {
    this[enums.FIELDS.OPTIONS] = options;
  }

  if (!_.isUndefined(revenue)) {
    this[enums.FIELDS.REVENUE] = revenue;
  }
}

exports.EventBase = EventBaseClass;

/**
 * Returns a digest of the event base.
 * Given identical property values, this method returns an identical digest.
 * @returns {string} A string digest of the event base properties.
 * @private
 */
EventBaseClass.prototype.digest = function(): string {
  var encode = function(k, v) {
    return encodeURIComponent(k) + '=' + encodeURIComponent(v);
  };
  var parts = [];
  parts.push(encode(enums.FIELDS.NAME, this[enums.FIELDS.NAME]));
  parts.push(encode(enums.FIELDS.TYPE, this[enums.FIELDS.TYPE]));
  if (this[enums.FIELDS.CATEGORY]) {
    parts.push(encode(enums.FIELDS.CATEGORY, this[enums.FIELDS.CATEGORY]));
  }
  if (this[enums.FIELDS.REVENUE]) {
    parts.push(encode(enums.FIELDS.REVENUE, this[enums.FIELDS.REVENUE]));
  }

  if (!this[enums.FIELDS.OPTIONS]) {
    return parts.join('&');
  }

  // Encode options.
  // Ensure a canonical order by encoding keys in sorted order.
  var options = this[enums.FIELDS.OPTIONS] || {};
  var optionKeys = _.filter(_.keys(options), function(k) {
    return options.hasOwnProperty(k);
  });
  optionKeys = optionKeys.sort();

  for (var i = 0; i < optionKeys.length; i++) {
    parts.push(encode(optionKeys[i], options[optionKeys[i]]));
  }

  return parts.join('&');
};

/**
 * Returns a hash of the event base.
 * @returns {string}
 */
EventBaseClass.prototype.hash = function(): string {
  if (this.hash_) {
    return this.hash_;
  }
  this.hash_ = hashing.hashToHex(
    hashing.toByteString(this.digest()), hashing.Seed.BEHAVIOR_EVENT);
  return this.hash_;
};

/**
 * Sets the hash of the event base. Used to populate the hash value from
 * persistent store and avoid recomputation.
 * @param {number} hash Hash value.
 */
EventBaseClass.prototype.setHash = function(hash:number) {
  this.hash_ = hash;
};

/**
 * Recomputes the hash value.
 * @private
 */
EventBaseClass.prototype.reHash = function() {
  this.hash_ = null;
  this.hash();
};

/**
 * Reports whether that is equal to this.
 * NOTE: This method does not perform a deep equal on the options field.
 * That field is expected to be shallow key-value pairs
 * i.e. the values are not themselves objects.
 * @param {module:behavior/event~EventBase} that
 * @returns {boolean} Whether that is equal to this.
 */
EventBaseClass.prototype.equals = function(that:import('core/behavior/event').EventBase): boolean {
  if (this.hash() !== that.hash()) {
    return false;
  }

  // Check shallow fields first.
  if (this[enums.FIELDS.NAME] !== that[enums.FIELDS.NAME] ||
      this[enums.FIELDS.TYPE] !== that[enums.FIELDS.TYPE] ||
      this[enums.FIELDS.CATEGORY] !== that[enums.FIELDS.CATEGORY] ||
      this[enums.FIELDS.REVENUE] !== that[enums.FIELDS.REVENUE]) {
    return false;
  }
  // Check options object.
  if (!this[enums.FIELDS.OPTIONS] && !that[enums.FIELDS.OPTIONS]) {
    return true;
  }

  var thisOptions = this[enums.FIELDS.OPTIONS] || {},
      thatOptions = that[enums.FIELDS.OPTIONS] || {};
  var thisKeys = _.filter(_.keys(thisOptions), function(k) {
    return thisOptions.hasOwnProperty(k);
  });
  var thatKeys = _.filter(_.keys(thatOptions), function(k) {
    return thatOptions.hasOwnProperty(k);
  });
  if (thisKeys.length !== thatKeys.length) {
    return false;
  }
  for (var i = 0; i < thisKeys.length; i++) {
    var key = thisKeys[i];
    if (!thatOptions.hasOwnProperty(key) || thisOptions[key] !== thatOptions[key]) {
      return false;
    }
  }
  return true;
};


/**
 * Returns value of field specified by keyPath.
 * @param {Array.<string>} keyPath Field key path.
 * @param {*} [defaultValue] Default value to return if field is not found.
 * @return {*} Value of field or undefined.
 */
EventBaseClass.prototype.getValueOrDefault = function(keyPath:string[], defaultValue?:any): any {
  var value = getFieldValue(this, keyPath);
  if (!_.isUndefined(value)) {
    return value;
  }
  return defaultValue;
};

/**
 * Sets field value; field must be one of those specified in enums.FIELDS
 * and owned by EventBaseClass.
 * @param {string} field
 * @param {*} value
 */
EventBaseClass.prototype.setFieldValue = function(field:string, value:any) {
  if (field === enums.FIELDS.NAME ||
      field === enums.FIELDS.TYPE ||
      field === enums.FIELDS.CATEGORY ||
      field === enums.FIELDS.REVENUE ||
      field === enums.FIELDS.OPTIONS) {
    this[field] = value;
    this.reHash();
  }
};

/**
 * Behavior event.
 *
 * @param {module:behavior/event~EventBase} eventBase Base event properties.
 * @param {number} timestamp Timestamp in milliseconds.
 * @param {number} [sessionId] Optional session id.
 * @param {number} [sessionIndex] Optional session index.
 * @constructor
 * @alias module:behavior/event~Event
 */
function EventClass(eventBase:import('core/behavior/event').EventBase, timestamp:number, sessionId?:number, sessionIndex?:number) {
  this.eventBase = eventBase;

  this[enums.FIELDS.TIME] = timestamp;

  if (!_.isUndefined(sessionId)) {
    this[enums.FIELDS.SESSION_ID] = sessionId;
  }

  if (!_.isUndefined(sessionIndex)) {
    this[enums.FIELDS.SESSION_INDEX] = sessionIndex;
  }
}

exports.Event = EventClass;

/**
 * Returns value of field specified by keyPath.
 * @param {string[]} keyPath Field key path.
 * @param {*} [defaultValue] Default value to return if field is not found.
 * @return {*} Value of field or undefined.
 */
EventClass.prototype.getValueOrDefault = function(keyPath:string[], defaultValue?:any): any {
  if (keyPath.length === 0) {
    return this;
  }
  // Check this first.
  var obj = {} as any;
  obj[enums.FIELDS.TIME] = this[enums.FIELDS.TIME];
  obj[enums.FIELDS.SESSION_ID] = this[enums.FIELDS.SESSION_ID];
  obj[enums.FIELDS.SESSION_INDEX] = this[enums.FIELDS.SESSION_INDEX];
  var value = getFieldValue(obj, keyPath);

  if (!_.isUndefined(value)) {
    return value;
  }

  return this.eventBase.getValueOrDefault(keyPath, defaultValue);
};


/**
 * Sets field value; field must be one of those specified in enums.FIELDS.
 * @param {string} field
 * @param {*} value
 */
EventClass.prototype.setFieldValue = function(field:string, value:any) {
  if (field === enums.FIELDS.TIME ||
      field === enums.FIELDS.SESSION_ID ||
      field === enums.FIELDS.SESSION_INDEX) {
    this[field] = value;
  } else {
    this.eventBase.setFieldValue(field, value);
  }
};

/**
 * Printable field names.
 * @type {Object.<string>}
 * @private
 */
var FIELD_NAMES = {
  'n': 'name',
  'y': 'type',
  'c': 'category',
  'r': 'revenue',
  's': 'session_id',
  'o': 'tags',
  'si': 'session_index',
};

/**
 * Returns a readable string representation of the event.
 * @returns {string} Readable string representation of the event.
 */
EventClass.prototype.readableEvent = function(): string {
  var formatValue = function(x) {
    if (_.isString(x)) {
      return '"' + x + '"';
    }
    return x;
  };

  var event = this;
  var parts = [];
  var fieldName, value;
  _.each([
    enums.FIELDS.NAME,
    enums.FIELDS.TYPE,
    enums.FIELDS.CATEGORY,
    enums.FIELDS.REVENUE,
    enums.FIELDS.SESSION_ID,
  ], function(field) {
    fieldName = FIELD_NAMES[field];
    value = event.getValueOrDefault([field]);
    if (!_.isUndefined(value)) {
      parts.push(fieldName + ': ' + formatValue(value));
    }
  });

  // Handle options and timestamp differently.
  var optionParts = [];
  fieldName = FIELD_NAMES[enums.FIELDS.OPTIONS];
  value = event.getValueOrDefault([enums.FIELDS.OPTIONS]);
  if (!_.isUndefined(value)) {
    _.each(value, function (v, k) {
      optionParts.push(k + ': ' + String(formatValue(v)));
    });
    parts.push(fieldName + ': {\n\t\t' + optionParts.join(',\n\t\t') + '\n\t}');
  }

  value = event.getValueOrDefault([enums.FIELDS.TIME]);
  if (_.isNumber(value)) {
    value = formatValue((new Date(value)).toString());
  }
  if (!_.isUndefined(value)) {
    var timeObjectFieldName = 'timestamp';
    parts.push(timeObjectFieldName + ': ' + value);
  }

  return '{\n' +
      '\t' + parts.join(',\n\t') + '\n' +
      '}';
};

/**
 * Returns event as a flat object with readable fields.
 * @param {Object} [prefs]
 * @param {boolean} prefs.revenueAsTag Whether revenue should be included as a tag, rather than as a top-level field.
 * @param {boolean} prefs.timeAsTimestamp Whether time should be returned as a Date object under the timestamp field,
 *                                        rather than as a number under the time field.
 * @returns {Object.<*>} Object with readable fields.
 */
EventClass.prototype.toObject = function(prefs?:any): Record<string, any> {
  var obj = {} as any;

  var event = this;
  var fieldName;
  var value;
  _.each([
    enums.FIELDS.NAME,
    enums.FIELDS.TYPE,
    enums.FIELDS.CATEGORY,
    enums.FIELDS.REVENUE,
    enums.FIELDS.OPTIONS,
    enums.FIELDS.SESSION_INDEX,
  ], function(field) {
    fieldName = FIELD_NAMES[field];
    value = event.getValueOrDefault([field], (field === enums.FIELDS.OPTIONS ? {} : undefined));
    if (!_.isUndefined(value)) {
      obj[fieldName] = value;
    }
  });

  var optionsFieldName = FIELD_NAMES[enums.FIELDS.OPTIONS];
  var revenueFieldName = FIELD_NAMES[enums.FIELDS.REVENUE];
  if (prefs && prefs.revenueAsTag && obj[revenueFieldName]) {
    obj[optionsFieldName] = obj[optionsFieldName] || {};
    obj[optionsFieldName][revenueFieldName] = obj[revenueFieldName];
    delete obj[revenueFieldName];
  }

  value = event.getValueOrDefault([enums.FIELDS.TIME]);
  if (_.isNumber(value)) {
    if (prefs && prefs.timeAsTimestamp) {
      var dateObjectFieldName = 'timestamp';
      obj[dateObjectFieldName] = new Date(value);
    } else {
      var timeFieldName = 'time';
      obj[timeFieldName] = value;
    }
  }

  return obj;
};
